import { LEVEL_COLORS, LEVEL_LABELS } from './assessmentConfig';
import config from '../../config';

export const HEALTH_SPAN_MAX_SCORE = 100;

/**
 * Sub-category display mode:
 * - 'simple'   = สีเดียว (SUB_CATEGORY_FIXED_COLOR), ไม่แสดง status text
 * - 'detailed' = แสดง status text + สีตาม level metadata (พฤติกรรมเดิม)
 */
export const SUB_CATEGORY_DISPLAY_MODE = 'simple';

/**
 * สีคงที่สำหรับ sub-category (ใช้เมื่อ mode = 'simple')
 */
export const SUB_CATEGORY_FIXED_COLOR = '#2C2C2C';

/**
 * สีสำหรับ sub-category ที่ไม่มีข้อมูล
 */
const NO_DATA_COLOR = '#A1A1A1';

/**
 * Get display props for sub-category item based on current display mode.
 * @param {Object} scoreData - score data from buildLandingScores
 * @param {boolean} hasData - whether the sub-category has valid score
 * @returns {{ statusText: string, barColor: string, valueColor: string }}
 */
export const getSubCategoryDisplayProps = (scoreData, hasData) => {
  if (!hasData) {
    return {
      statusText: 'ไม่มีข้อมูล',
      barColor: NO_DATA_COLOR,
      valueColor: NO_DATA_COLOR,
    };
  }

  if (SUB_CATEGORY_DISPLAY_MODE === 'simple') {
    return {
      statusText: '',
      barColor: SUB_CATEGORY_FIXED_COLOR,
      valueColor: SUB_CATEGORY_FIXED_COLOR,
    };
  }

  // detailed mode
  return {
    statusText: scoreData.statusText || '',
    barColor: scoreData.barColor || SUB_CATEGORY_FIXED_COLOR,
    valueColor: scoreData.barColor || SUB_CATEGORY_FIXED_COLOR,
  };
};

/**
 * Config flag: ถ้า true = score 0 ถือว่า "ไม่มีข้อมูล"
 * ถ้า false = score 0 ถือว่า "มีข้อมูล"
 * สามารถเปลี่ยนค่านี้เพื่อเปิด/ปิด feature
 */
export const TREAT_ZERO_AS_NO_DATA = false;

/**
 * ตรวจสอบว่า score ถือว่า "มีข้อมูล" หรือไม่
 * @param {number|null|undefined} score
 * @returns {boolean}
 */
export const hasValidScore = (score) => {
  if (score === null || score === undefined) return false;
  if (TREAT_ZERO_AS_NO_DATA && score === 0) return false;
  return true;
};

export const UI_CATEGORY_TO_METADATA_KEY = {
  nutrition: 'health_span_lifestyle',
  metabolism: 'health_span_metabolic_health',
  exercise: 'health_span_physical_fitness',
  stress: 'health_span_mental_emotional_health',
  gut: 'health_span_gut_health',
  skin: 'health_span_skin_health',
  health_span: 'health_span_total_score',
};

/**
 * UI landing subcategory key → API score_by_sub_category key
 */
export const SUB_CATEGORY_KEY_MAP = {
  // Lifestyle / nutrition
  nutrition: 'NutritionQuality',
  water: 'Hydration',
  sleep: 'Sleep',
  behavior: 'HealthBehavior',
  // MetabolicHealth / metabolism
  bmi: 'BMI',
  waist: 'WaistCircumference',
  bodyFat: 'BodyFatPercentage',
  // PhysicalFitness / exercise
  walking: 'DailySteps',
  exerciseBehavior: 'ExerciseBehavior',
  muscle: 'MuscleMass',
  exerciseConfidence: 'ExerciseConfidence',
  // MentalEmotionalHealth / stress
  stressLevel: 'StressData',
  // GutHealth / gut
  gutBalance: 'GutBalanceData',
  // SkinHealth / skin
  wrinkle: 'Wrinkles',
  darkSpot: 'DarkSpots',
  sagging: 'SkinLaxity',
  skinOther: 'Other',
};

/**
 * Resolve tip image path from metadata to a usable URL.
 * - absolute http(s) URL → use as-is
 * - relative S3 key/path → prefix with config.S3_URL
 * @param {string|null|undefined} image
 * @param {string} [fallback='']
 * @returns {string}
 */
export const resolveTipImage = (image, fallback = '') => {
  if (!image) return fallback;
  if (/^https?:\/\//i.test(image)) return image;
  const path = String(image).replace(/^\//, '');
  const base = (config.S3_URL || '').replace(/\/$/, '');
  return `${base}/${path}`;
};

const normalizeTip = (tip) => {
  if (!tip) return null;
  return {
    ...tip,
    image: resolveTipImage(tip.image),
  };
};

const fallbackStatusFromScore = (score) => {
  if (score === null || score === undefined || Number.isNaN(score)) {
    return {
      statusText: 'ไม่มีข้อมูล',
      barColor: '#A1A1A1',
      tip: null,
      maxScore: HEALTH_SPAN_MAX_SCORE,
    };
  }

  const clamped = Math.max(0, Math.min(HEALTH_SPAN_MAX_SCORE, score));
  const index =
    clamped >= HEALTH_SPAN_MAX_SCORE
      ? LEVEL_LABELS.length - 1
      : Math.floor(clamped / 20);

  return {
    statusText: LEVEL_LABELS[index] || LEVEL_LABELS[0],
    barColor: LEVEL_COLORS[index] || LEVEL_COLORS[0],
    tip: null,
    maxScore: HEALTH_SPAN_MAX_SCORE,
  };
};

/**
 * Find the nearest level when score falls in a gap between levels.
 * Prefers the lower level (worse status) when score is in a gap.
 * @param {number} score
 * @param {Array} levels - sorted by score_from ascending
 * @returns {Object|null} nearest level or null
 */
const findNearestLevel = (score, levels) => {
  if (!levels || levels.length === 0) return null;

  // Sort levels by score_from to ensure correct order
  const sorted = [...levels].sort((a, b) => a.score_from - b.score_from);

  // Find the level where score is just above score_to (gap below)
  // or just below score_from (gap above)
  let bestMatch = null;
  let minDistance = Infinity;

  for (const level of sorted) {
    // Check distance to this level's range
    if (score < level.score_from) {
      const dist = level.score_from - score;
      if (dist < minDistance) {
        minDistance = dist;
        bestMatch = level;
      }
    } else if (score > level.score_to) {
      const dist = score - level.score_to;
      if (dist < minDistance) {
        minDistance = dist;
        bestMatch = level;
      }
    }
  }

  return bestMatch;
};

/**
 * Resolve label/color/tip for a score using API metadata levels.
 * @param {number|null|undefined} score
 * @param {Object|null|undefined} metadata - metadata object from score-category-metadata API
 * @returns {{ statusText: string, barColor: string, tip: Object|null, maxScore: number }}
 */
export const resolveLevel = (score, metadata = null) => {
  const maxScore = metadata?.max_score || HEALTH_SPAN_MAX_SCORE;
  const emptyState = metadata?.empty_state || {
    label: 'ไม่มีข้อมูล',
    color: '#A1A1A1',
  };

  if (score === null || score === undefined || Number.isNaN(score)) {
    return {
      statusText: emptyState.label || 'ไม่มีข้อมูล',
      barColor: emptyState.color || '#A1A1A1',
      tip: null,
      maxScore,
    };
  }

  const levels = metadata?.levels;
  if (!levels || !Array.isArray(levels) || levels.length === 0) {
    return fallbackStatusFromScore(score);
  }

  // First try exact match
  let matched = levels.find(
    (level) => score >= level.score_from && score <= level.score_to
  );

  // If no exact match (score in gap), find nearest level
  if (!matched) {
    matched = findNearestLevel(score, levels);
  }

  // Final fallback to first level (worst case) if still no match
  if (!matched) {
    matched = levels[0];
  }

  return {
    statusText: matched?.label || emptyState.label || 'ไม่มีข้อมูล',
    barColor: matched?.color || emptyState.color || '#A1A1A1',
    tip: normalizeTip(matched?.tip),
    maxScore,
  };
};

/**
 * @deprecated Prefer resolveLevel with metadata
 */
export const getStatusFromScore = (score) => {
  const result = fallbackStatusFromScore(score);
  return {
    statusText: result.statusText,
    barColor: result.barColor,
  };
};

/**
 * Build landing list scores from HealthSpanProfile.scoreDetails + metadata
 * @param {import('../../model/HealthSpanProfile.model').HealthSpanProfile|null|undefined} profile
 * @param {string} uiCategory - nutrition | metabolism | exercise | stress | gut | skin
 * @param {Array<{ key: string, disabled?: boolean, defaultMaxScore?: number }>} categories
 * @param {Object|null|undefined} metadata - metadata from score-category-metadata API
 */
export const buildLandingScores = (
  profile,
  uiCategory,
  categories = [],
  metadata = null
) => {
  const detail = profile?.scoreDetails?.[uiCategory] ?? null;
  const subCategories = detail?.subCategories || {};
  // Use score_by_category (via categoryScores) as the primary header score,
  // fall back to score_details entry score if categoryScores not available
  const categoryScoreRaw = profile?.categoryScores?.[uiCategory];
  const categoryScore =
    categoryScoreRaw !== undefined && categoryScoreRaw !== null
      ? categoryScoreRaw
      : (detail?.score !== undefined && detail?.score !== null ? detail.score : null);

  // Determine if category actually has data:
  // - has score_details entry with non-empty sub_categories, OR
  // - categoryScore > 0
  const hasSubData = Object.keys(subCategories).length > 0;
  const effectiveCategoryScore = (categoryScore !== null && (categoryScore > 0 || hasSubData))
    ? categoryScore
    : null;
  const categoryStatus = resolveLevel(effectiveCategoryScore, metadata);

  const scores = {};

  categories.forEach((category) => {
    const apiKey = SUB_CATEGORY_KEY_MAP[category.key];
    const rawScore = apiKey ? subCategories[apiKey] : undefined;
    const hasScore = hasValidScore(rawScore);
    const subMeta = apiKey ? metadata?.sub_categories?.[apiKey] : null;
    const subMaxScore = subMeta?.max_score || HEALTH_SPAN_MAX_SCORE;
    const subLevels = subMeta?.levels || null;

    // Create a pseudo-metadata object for sub-category with its own max_score
    const subMetaForResolve = subMeta ? {
      max_score: subMaxScore,
      levels: subLevels,
      empty_state: metadata?.empty_state,
    } : null;

    if (!hasScore) {
      const empty = resolveLevel(null, subMetaForResolve);
      scores[category.key] = {
        score: null,
        maxScore: category.disabled
          ? category.defaultMaxScore || subMaxScore
          : subMaxScore,
        statusText: empty.statusText,
        barColor: empty.barColor,
        tip: null,
      };
      return;
    }

    const resolved = resolveLevel(rawScore, subMetaForResolve);
    scores[category.key] = {
      score: rawScore,
      maxScore: subMaxScore,
      statusText: resolved.statusText,
      barColor: resolved.barColor,
      tip: resolved.tip,
    };
  });

  return {
    categoryScore: effectiveCategoryScore,
    categoryStatus,
    scores,
  };
};
